import React, { type JSX } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

export interface Props {
    tabs: {
        // The display text or element for the tab.
        text: JSX.Element | string;
        // Where the tab navigates.
        href?: string;
        // The click handler for the tab. This is used when href is not provided.
        onClick?: () => void;
        active: boolean;
        key?: string;
        icon?: JSX.Element;
        className?: string;
        disabled?: boolean;
    }[];
    className?: string;
    id?: string;
}

const tabStyles = {
    base: "flex h-fit items-center gap-1.5 rounded-xl border px-2 py-[3px] font-medium",
    active: "border-stone-100 bg-stone-100 text-stone-950 hover:bg-stone-100",
    inactive: "border-transparent bg-transparent text-stone-600 hover:bg-stone-50",
};

export default function TabBar({ tabs = [], className = "", id }: Props) {
    return (
        <div id={id} className={twMerge("inline-flex items-center gap-1", className)}>
            {tabs.map((tab, index) => {
                if (tab.disabled) {
                    return null;
                }

                const classes = twMerge(tabStyles.base, tab.active ? tabStyles.active : tabStyles.inactive, tab.className);
                const contents = (
                    <>
                        {tab.icon && (
                            <span className="inline-flex h-4 w-4 items-center justify-center">
                                {React.cloneElement(tab.icon as React.ReactElement<any>, {
                                    className: twMerge("stroke-stone-400", (tab.icon.props as any)?.className),
                                })}
                            </span>
                        )}
                        {tab.text && <span className="whitespace-nowrap">{tab.text}</span>}
                    </>
                );
                const key = tab.key ?? `${index}`;

                if (tab.href !== undefined) {
                    return (
                        <Link key={key} to={tab.href} onClick={tab.onClick} className={classes}>
                            {contents}
                        </Link>
                    );
                }

                return (
                    <button key={key} type="button" onClick={tab.onClick} className={classes}>
                        {contents}
                    </button>
                );
            })}
        </div>
    );
}
